import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";

// Tanggal "hari ini" versi SERVER (WIB, dibaca dari DB lewat /api/meta), bukan jam browser.
// Jam browser yang mundur akan menyimpan tanggal lampau sebagai "hari ini" dan server tidak bisa
// membedakannya dari backdate yang sah — jadi nilai ini dipakai untuk default sekaligus batas atas.
// ponytail: cache-nya per-muat-halaman, jadi tab yang dibiarkan terbuka melewati tengah malam
// masih memakai tanggal kemarin (masih sah sebagai backdate, cuma bukan "hari ini"). Kalau itu
// mengganggu, hapus `hariIniSiap` dan muat ulang tiap dialog dibuka — satu GET, tanpa biaya nyata.
// Dimuat saat halaman dipasang (lihat effect [] di bawah), BUKAN saat dialog dibuka: dulu
// permintaannya baru berangkat setelah form tampil, jadi kolom tanggal kosong selama satu
// round-trip dan user menyimpulkan tanggal tidak terisi otomatis. Dialog baru bisa dibuka setelah
// user menekan Edit, dan itu selalu sesudah halaman ini selesai memuat barisnya.
// Kegagalan TIDAK di-cache: kalau hasil 401/putus disimpan, tanggal tidak akan pernah terisi lagi
// di halaman ini — lebih baik satu percobaan ulang saat dialog dibuka.
let hariIniSiap;   // nilai yang sudah termuat, dibaca sinkron saat dialog dibuka
let hariIniJanji;  // permintaan yang sedang berjalan
const muatHariIni = () => {
  if (!hariIniJanji) {
    hariIniJanji = api.get("/meta")
      .then((r) => (hariIniSiap = r.data.today))
      .catch(() => { hariIniJanji = undefined; return null; });
  }
  return hariIniJanji;
};

const Field = ({ f, row, value, onChange, prefix, disabled, today }) => {
  // `optionsFor` membuat opsi bergantung pada baris yang diedit (Grid: 'Terupload' satu arah).
  const opsi = f.optionsFor ? f.optionsFor(row) : f.options;
  return (
  <div className="space-y-1.5">
    <Label className="font-mono text-[10px] uppercase tracking-widest text-slate-400">{f.label}</Label>
    {f.type === "status" ? (
      <Select value={value ?? opsi[0]} onValueChange={onChange} disabled={disabled}>
        <SelectTrigger data-testid={`${prefix}-form-${f.key}`}><SelectValue /></SelectTrigger>
        <SelectContent className="z-[2000]">
          {opsi.map((o) => <SelectItem key={o} value={o} data-testid={`${prefix}-form-${f.key}-opt-${o.replace(/\s/g, "-").toLowerCase()}`}>{o}</SelectItem>)}
        </SelectContent>
      </Select>
    ) : f.type === "number" ? (
      // step="any" wajib: step bawaan 1 membuat browser menolak desimal dan memblokir submit.
      <Input data-testid={`${prefix}-form-${f.key}`} type="number" step="any" min="0" max="100" value={value ?? ""} onChange={(e) => onChange(e.target.value)} disabled={disabled} className="bg-[#0B0F17]" />
    ) : (
      <Input data-testid={`${prefix}-form-${f.key}`} type={f.type === "date" ? "date" : "text"} placeholder={f.hint}
        // `today`: tanggal ukur hanya boleh mundur. `max` menghentikan pemilih tanggal di browser;
        // server menegakkan aturan yang sama (crud.js `backdate`), karena form bukan satu-satunya klien.
        max={f.hariIni ? today || undefined : undefined}
        // `wajib`: browser menahan submit kosong sebelum sempat ke server. Server menolak juga
        // (crud.js `titik`), jadi ini hanya jalur cepat, bukan satu-satunya penjaga.
        required={f.wajib || undefined}
        value={value ?? ""} onChange={(e) => onChange(e.target.value)} disabled={disabled} className="bg-[#0B0F17]" />
    )}
  </div>
  );
};

// Kolom turunan (mis. `luas_ha`, hasil hitung geometri di server) tidak punya kolom DB, jadi
// tidak boleh jadi input form — server mengabaikannya dan mengetiknya cuma menyesatkan.
const dapatDiisi = (c) => c.type !== "luas";

export const EntityFormDialog = ({ open, onOpenChange, columns, initial, onSubmit, title, prefix, idKey }) => {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  // Identitas baris yang dikunci saat edit — bukan selalu kolom pertama (Flight menampilkan
  // Block ID di depan padahal kuncinya Flight ID).
  // `initial` hanya terisi di mode edit, jadi hanya edit yang mengunci ID. Nilainya
  // tetap ada di state `form` supaya PUT tetap mengirim idField — server menolaknya
  // kalau kosong (crud.js clean()).
  // Kolom lain bisa ikut terkunci lewat `lockOnEdit` di COLUMNS (Block ID di Flight). Itu
  // keputusan UI saja, sama seperti tombol Tambah/Hapus yang disembunyikan di Grid dan CP:
  // rutenya tetap hidup, jadi PUT langsung masih boleh mengubahnya.
  const identitas = idKey ?? columns[0]?.key;
  const lockId = !!initial;
  const [today, setToday] = useState(hariIniSiap ?? null);

  // Mulai permintaan /meta saat halaman dipasang, jauh sebelum dialog pertama dibuka. Kalau
  // nilainya sudah ada saat dibuka, `hariIniSiap` dibaca SINKRON di bawah sehingga tanggal
  // terisi pada render pertama — tidak ada jeda kosong yang terlihat.
  useEffect(() => {
    let batal = false;
    muatHariIni().then((t) => { if (t && !batal) setToday(t); });
    return () => { batal = true; };
  }, []);

  useEffect(() => {
    // Kolom status/number diisi default supaya POST tidak mengirim `undefined` — crud.js
    // mengubah undefined jadi null, dan null ditolak untuk kolom ber-enum.
    // Kolom tanggal ber-`hariIni` yang masih KOSONG diisi tanggal server — juga di mode edit,
    // karena CP memang edit-only: baris tanpa tanggal ukur adalah baris yang belum diukur, dan
    // itulah yang tanggalnya diisi sekarang. Baris yang SUDAH punya tanggal tidak disentuh —
    // menimpanya dengan hari ini berarti menghapus data asli setiap kali dialog dibuka.
    // Urutannya penting: nilai baris lebih dulu, default hanya MENGISI yang kosong. Versi lama
    // membangun objek default lalu menimpanya dengan `dasar`, jadi `tanggal_ukur: null` milik
    // baris menimpa default dan form tetap kosong.
    if (!open) return undefined;
    // Salinan, bukan referensi: `initial` adalah baris milik EntityPage, dan default tanggal di
    // bawah ditulis ke `dasar` — memutasinya berarti mengubah data yang sedang ditampilkan tabel.
    const dasar = { ...(initial || Object.fromEntries(columns.filter((c) => c.type === "status" || c.type === "number").map((c) => [c.key, c.type === "number" ? "" : c.options[0]]))) };
    const perlu = columns.filter((c) => c.hariIni && !dasar[c.key]).map((c) => c.key);
    // Nilai yang sudah termuat dipakai SINKRON di sini: setForm di bawah langsung membawa
    // tanggalnya, jadi kolom tidak pernah tampil kosong lalu terisi belakangan.
    if (hariIniSiap && perlu.length) {
      for (const k of perlu) dasar[k] = hariIniSiap;
    }
    setForm(dasar);
    // Masih ada permintaan yang berjalan hanya kalau halaman ini belum selesai memuatnya —
    // kemungkinan kecil, tapi tanpa cabang ini kolomnya akan kosong selamanya.
    if (perlu.length && !hariIniSiap) {
      let batal = false;
      muatHariIni().then((t) => {
        if (!t || batal) return;
        setToday(t);
        setForm((s) => ({ ...s, ...Object.fromEntries(perlu.map((k) => [k, t])) }));
      });
      return () => { batal = true; };
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initial, columns]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try { await onSubmit(form); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="z-[1500] max-w-xl bg-[#131B29]" data-testid={`${prefix}-form-dialog`}>
        <DialogHeader><DialogTitle className="font-display text-2xl">{title}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-5">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {columns.filter(dapatDiisi).map((f) => <Field key={f.key} f={f} row={initial} prefix={prefix} today={today} disabled={lockId && (f.key === identitas ? !f.bisaUbahId : !!f.lockOnEdit)} value={form[f.key]} onChange={(v) => setForm((s) => ({ ...s, [f.key]: v }))} />)}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid={`${prefix}-form-cancel`}>Batal</Button>
            <Button type="submit" disabled={saving} data-testid={`${prefix}-form-submit`}>{saving ? "Menyimpan..." : "Simpan"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
